import React, { useState, useEffect } from 'react';

import { 

  Container, Form, Button, Spinner, Alert, 

  Tabs, Tab, ListGroup, Badge, Card

} from 'react-bootstrap';

import { videoAPI } from '../services/api';


const VideoAnalysis = () => {

  const [video, setVideo] = useState(null);

  const [videoName, setVideoName] = useState('');

  const [activeTab, setActiveTab] = useState('labels');

  const [loading, setLoading] = useState(false);

  const [processingJob, setProcessingJob] = useState(false);

  const [jobId, setJobId] = useState('');

  const [s3Key, setS3Key] = useState('');

  const [error, setError] = useState(null);

  const [results, setResults] = useState(null);

  const [pollingInterval, setPollingInterval] = useState(null);


  // Clear polling interval on component unmount

  useEffect(() => {

    return () => {

      if (pollingInterval) clearInterval(pollingInterval);

    };

  }, [pollingInterval]);


  // Handle file selection

  const handleVideoChange = (e) => {

    if (e.target.files[0]) {

      const selectedVideo = e.target.files[0];

      setVideo(selectedVideo);

      setVideoName(selectedVideo.name);

      

      // Reset results when a new video is selected

      setResults(null);

      setJobId('');

      setS3Key('');

      if (pollingInterval) clearInterval(pollingInterval);

    }

  };


  // Start video analysis based on the active tab

  const startVideoAnalysis = async () => {

    if (!video) return;

    

    setLoading(true);

    setProcessingJob(false);

    setError(null);

    setResults(null);

    

    try {

      let response;

      

      if (activeTab === 'labels') {

        response = await videoAPI.startLabelDetection(video);

      } else if (activeTab === 'persons') {

        response = await videoAPI.startPersonTracking(video);

      }

      

      setJobId(response.data.jobId);

      setS3Key(response.data.s3Key);

      setProcessingJob(true);

      

      // Set up polling for job completion

      const interval = setInterval(() => {

        checkJobStatus(response.data.jobId);

      }, 5000); // Check every 5 seconds

      

      setPollingInterval(interval);

      

    } catch (err) {

      setError(`Error starting analysis: ${err.response?.data?.error || err.message}`);

      setLoading(false);

    }

  };


  // Check job status

  const checkJobStatus = async (jobId) => {

    try {

      let response;

      

      if (activeTab === 'labels') {

        response = await videoAPI.getLabelDetection(jobId);

      } else if (activeTab === 'persons') {

        response = await videoAPI.getPersonTracking(jobId);

      }

      

      if (response.data.JobStatus === 'SUCCEEDED') {

        // Job completed successfully

        clearInterval(pollingInterval);

        setPollingInterval(null);

        setProcessingJob(false);

        setLoading(false);

        setResults(response.data);

      } else if (response.data.JobStatus === 'FAILED') {

        // Job failed

        clearInterval(pollingInterval);

        setPollingInterval(null);

        setProcessingJob(false);

        setLoading(false);

        setError(`Analysis job failed: ${response.data.StatusMessage || 'Unknown error'}`);

      }

      // If status is still IN_PROGRESS, continue polling

      

    } catch (err) {

      clearInterval(pollingInterval);

      setPollingInterval(null);

      setProcessingJob(false);

      setLoading(false);

      setError(`Error checking job status: ${err.response?.data?.error || err.message}`);

    }

  };


  // Format timestamp (milliseconds) to minutes:seconds

  const formatTimestamp = (timestamp) => {

    const totalSeconds = Math.floor(timestamp / 1000);

    const minutes = Math.floor(totalSeconds / 60);

    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;

  };


  // Render label detection results

  const renderLabelResults = () => {

    if (!results) return null;

    

    // Group labels by timestamp

    const labelsByTimestamp = {};

    

    results.Labels.forEach(label => {

      const timestamp = label.Timestamp;

      if (!labelsByTimestamp[timestamp]) {

        labelsByTimestamp[timestamp] = [];

      }

      labelsByTimestamp[timestamp].push(label);

    });

    

    return (

      <div className="result-container">

        <h4>Detected Labels</h4>

        <p>Found labels across {Object.keys(labelsByTimestamp).length} timestamps</p>

        

        {Object.entries(labelsByTimestamp)

          .sort(([timestampA], [timestampB]) => parseInt(timestampA) - parseInt(timestampB))

          .map(([timestamp, labels]) => (

            <Card key={timestamp} className="mb-3">

              <Card.Header>

                Timestamp: {formatTimestamp(parseInt(timestamp))}

              </Card.Header>

              <ListGroup variant="flush">

                {labels.map((label, index) => (

                  <ListGroup.Item key={index}>

                    <div className="d-flex justify-content-between align-items-center">

                      <span>{label.Label.Name}</span>

                      <Badge bg="primary" pill>

                        {label.Label.Confidence.toFixed(2)}%

                      </Badge>

                    </div>

                  </ListGroup.Item>

                ))}

              </ListGroup>

            </Card>

          ))}

      </div>

    );

  };


  // Render person tracking results

  const renderPersonResults = () => {

    if (!results) return null;

    

    return (

      <div className="result-container">

        <h4>Person Tracking Results</h4>

        <p>Tracked {results.Persons.length} person instances in the video</p>

        

        {/* Group persons by person index */}

        {Array.from(new Set(results.Persons.map(p => p.Person.Index)))

          .sort((a, b) => a - b)

          .map(personIndex => {

            const personInstances = results.Persons.filter(p => p.Person.Index === personIndex);

            

            return (

              <Card key={personIndex} className="mb-3">

                <Card.Header>

                  Person #{personIndex}

                </Card.Header>

                <Card.Body>

                  <p>Appears in {personInstances.length} frames</p>

                  

                  <h6>Timeline:</h6>

                  <ListGroup>

                    {personInstances

                      .sort((a, b) => a.Timestamp - b.Timestamp)

                      .slice(0, 10) // Limit to first 10 instances to avoid overwhelming display

                      .map((instance, index) => (

                        <ListGroup.Item key={index}>

                          <div className="d-flex justify-content-between align-items-center">

                            <span>Time: {formatTimestamp(instance.Timestamp)}</span>

                            <Badge bg="primary" pill>

                              Confidence: {instance.Person.Confidence.toFixed(2)}%

                            </Badge>

                          </div>

                        </ListGroup.Item>

                      ))}

                  </ListGroup>

                  

                  {personInstances.length > 10 && (

                    <p className="text-muted mt-2">

                      Showing 10 of {personInstances.length} instances...

                    </p>

                  )}

                </Card.Body>

              </Card>

            );

          })}

      </div>

    );

  };


  return (

    <Container>

      <h2 className="mb-4">Video Analysis</h2>

      

      <Form>

        <Form.Group className="mb-3">

          <Form.Label>Upload a video for analysis</Form.Label>

          <Form.Control

            type="file"

            accept="video/*"

            onChange={handleVideoChange}

          />

          <Form.Text className="text-muted">

            Supported formats: MP4, MOV, and other common video formats.

            Note: Large videos may take a while to upload and process.

          </Form.Text>

        </Form.Group>

        

        {videoName && (

          <Alert variant="info">

            Selected video: {videoName}

          </Alert>

        )}

        

        <Tabs

          activeKey={activeTab}

          onSelect={(tab) => setActiveTab(tab)}

          className="mt-4"

        >

          <Tab eventKey="labels" title="Label Detection">

            <div className="mt-3">

              <p>Detect objects, scenes, concepts, and activities in your video</p>

            </div>

          </Tab>

          <Tab eventKey="persons" title="Person Tracking">

            <div className="mt-3">

              <p>Track people throughout your video and analyze their movements</p>

            </div>

          </Tab>

        </Tabs>

        

        <div className="mt-3">

          <Button

            variant="primary"

            onClick={startVideoAnalysis}

            disabled={!video || loading || processingJob}

          >

            {loading ? (

              <>

                <Spinner

                  as="span"

                  animation="border"

                  size="sm"

                  role="status"

                  aria-hidden="true"

                  className="me-2"

                />

                Uploading Video...

              </>

            ) : processingJob ? (

              <>

                <Spinner

                  as="span"

                  animation="border"

                  size="sm"

                  role="status"

                  aria-hidden="true"

                  className="me-2"

                />

                Processing Video...

              </>

            ) : (

              `Start ${activeTab === 'labels' ? 'Label Detection' : 'Person Tracking'}`

            )}

          </Button>

        </div>

      </Form>

      

      {error && (

        <Alert variant="danger" className="mt-4">

          {error}

        </Alert>

      )}

      

      {processingJob && (

        <Alert variant="info" className="mt-4">

          <div className="d-flex align-items-center">

            <Spinner

              animation="border"

              size="sm"

              role="status"

              aria-hidden="true"

              className="me-2"

            />

            <div>

              Video analysis job in progress. This may take several minutes depending on the video length.

              <div className="job-info mt-2">

                <strong>Job ID:</strong> {jobId}

              </div>

            </div>

          </div>

        </Alert>

      )}

      

      <div className="mt-4">

        {activeTab === 'labels' && renderLabelResults()}

        {activeTab === 'persons' && renderPersonResults()}

      </div>

    </Container>

  );

};


export default VideoAnalysis;

